// ft3d-cell-light.ts — the client half of the server per-cell light receipt (Stage 1 of
// plans/ft3d-interior-ambient-local-light-plan-2026-08-27.md).
//
// The server (`ft3d_minimap_projection.php`) emits one packed R8 tier lattice per physical floor. This
// module only DECODES and PRESENTS it: it never derives light, visibility, or room state from the
// encounter. Absent cells are tier 0 ("unseen"/no data) and are rendered untouched — the unseen
// veil, not this field, hides withheld space. Pure and DOM-free so a node test can cover it.

export const CELL_LIGHT_GRID_SCHEMA = 'ft.ft3d.cell-light-grid.v1';
/** 1024 × 1024 five-foot cells (≈ one square mile); anything larger is a malformed receipt. */
export const CELL_LIGHT_MAX_CELLS = 1024 * 1024;
export const CELL_LIGHT_MAX_TIER = 5;

export const CELL_LIGHT_TIER = Object.freeze({
  unseen: 0,
  pitchBlack: 1,
  magicalDarkness: 2,
  dim: 3,
  bright: 4,
  sensed: 5,
});

export interface Ft3dCellLightFloor {
  /** Absolute floor elevation (ft) for this physical room span. */
  floor: number;
  originX: number;
  originY: number;
  columns: number;
  rows: number;
  cellCount: number;
  physical: string;
  display: string;
}

export interface Ft3dCellLightGrid {
  schema: typeof CELL_LIGHT_GRID_SCHEMA;
  view: 'gm' | 'player';
  revision: number;
  epoch: string;
  cellSizeFt: number;
  tiers: Record<string, number>;
  baseLight?: {
    override: string | null;
    effective: string | null;
    authoredRooms: boolean;
    daylightWindows: number;
  };
  floors: Ft3dCellLightFloor[];
  /** Stage 3: observable light sources (position/radius only); absent on older payloads. */
  sources?: unknown[];
  queryOnly: true;
  writes: 0;
}

export function isFt3dCellLightGrid(value: unknown): value is Ft3dCellLightGrid {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  if (row.schema !== CELL_LIGHT_GRID_SCHEMA || row.queryOnly !== true || row.writes !== 0) return false;
  if (row.view !== 'gm' && row.view !== 'player') return false;
  if (!Number.isSafeInteger(row.revision) || (row.revision as number) < 0) return false;
  if (typeof row.cellSizeFt !== 'number' || !(row.cellSizeFt > 0)) return false;
  if (typeof row.epoch !== 'string' || row.epoch === '') return false;
  if (!Array.isArray(row.floors)) return false;
  return row.floors.every(floor => {
    const entry = floor as Record<string, unknown>;
    return entry && typeof entry === 'object'
      && typeof entry.floor === 'number' && Number.isFinite(entry.floor)
      && typeof entry.originX === 'number' && Number.isFinite(entry.originX)
      && typeof entry.originY === 'number' && Number.isFinite(entry.originY)
      && Number.isSafeInteger(entry.columns) && (entry.columns as number) > 0
      && Number.isSafeInteger(entry.rows) && (entry.rows as number) > 0
      && (entry.columns as number) * (entry.rows as number) <= CELL_LIGHT_MAX_CELLS
      && typeof entry.physical === 'string' && typeof entry.display === 'string';
  });
}

export function cloneFt3dCellLightGrid(grid: Ft3dCellLightGrid): Ft3dCellLightGrid {
  return {
    ...grid,
    tiers: { ...grid.tiers },
    ...(grid.baseLight ? { baseLight: { ...grid.baseLight } } : {}),
    // Allow-list the source fields: a receipt can never carry provenance into client state.
    ...(Array.isArray(grid.sources) ? {
      sources: grid.sources.flatMap(source => {
        const row = source as Record<string, unknown> | null;
        if (!row || typeof row !== 'object') return [];
        return [{ x: row.x, y: row.y, z: row.z, radius: row.radius, magical: row.magical }];
      }),
    } : {}),
    floors: grid.floors.map(floor => ({ ...floor })),
  };
}

function decodeBase64(value: string): Uint8Array {
  if (typeof atob === 'function') {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
    return bytes;
  }
  const nodeBuffer = (globalThis as { Buffer?: { from(input: string, encoding: string): Uint8Array } }).Buffer;
  if (nodeBuffer) return new Uint8Array(nodeBuffer.from(value, 'base64'));
  throw new Error('no base64 decoder available');
}

/**
 * Decode one channel of a floor. A short/long payload is a contract violation: return a zero
 * (unseen) lattice rather than trusting a partial one, so a malformed receipt can never brighten.
 */
export function decodeCellLightFloor(floor: Ft3dCellLightFloor, channel: 'physical' | 'display'): Uint8Array {
  const expected = floor.columns * floor.rows;
  let decoded: Uint8Array;
  try {
    decoded = decodeBase64(floor[channel]);
  } catch {
    return new Uint8Array(expected);
  }
  if (decoded.length !== expected) return new Uint8Array(expected);
  // An unknown tier code is a contract violation too — the LUT's spare entries are identity, so a
  // stray 6/7/255 would read as fully lit. Reject the whole channel to unseen.
  for (let index = 0; index < decoded.length; index += 1) {
    if (decoded[index]! > CELL_LIGHT_MAX_TIER) return new Uint8Array(expected);
  }
  return decoded;
}

/** Ordering by LIGHT for overlap resolution: unseen < magical < pitch < sensed < dim < bright. */
export function cellLightBrightnessRank(tier: number): number {
  switch (tier) {
    case CELL_LIGHT_TIER.magicalDarkness: return 1;
    case CELL_LIGHT_TIER.pitchBlack: return 2;
    case CELL_LIGHT_TIER.sensed: return 3;
    case CELL_LIGHT_TIER.dim: return 4;
    case CELL_LIGHT_TIER.bright: return 5;
    default: return 0;
  }
}

/** Tier at a floor-plane position, or 0 when outside the lattice. */
export function cellLightTierAt(floor: Ft3dCellLightFloor, decoded: Uint8Array, xFt: number, yFt: number, cellSizeFt: number): number {
  const column = Math.floor((xFt - floor.originX) / cellSizeFt);
  const row = Math.floor((yFt - floor.originY) / cellSizeFt);
  if (column < 0 || row < 0 || column >= floor.columns || row >= floor.rows) return CELL_LIGHT_TIER.unseen;
  return decoded[row * floor.columns + column] ?? CELL_LIGHT_TIER.unseen;
}

/**
 * A later receipt must never be replaced by an earlier one that arrives late. Revisions are
 * monotonic per encounter AND per view; the caller keys `previous` by view so returning to the GM
 * bootstrap projection after a caster view is not mistaken for an out-of-order caster receipt.
 */
export function acceptCellLightRevision(previous: number | null | undefined, next: number): boolean {
  if (!Number.isFinite(next)) return false;
  if (previous === null || previous === undefined || !Number.isFinite(previous)) return true;
  return next >= previous;
}

/**
 * Floors to rasterize for the selected numeric floor band.
 */
export function selectCellLightFloors(
  grid: Ft3dCellLightGrid,
  activeFloorFt: number | null,
): Ft3dCellLightFloor[] {
  if (!Number.isFinite(activeFloorFt)) return [];
  return grid.floors.filter(floor => Math.abs(floor.floor - Number(activeFloorFt)) < 0.01);
}

/**
 * Per-tier grade in LINEAR light, applied before tone mapping: [r, g, b, desaturate]. Tier 0 (no
 * data / unseen) and bright are identity. The GM table is deliberately lifted so the GM can still
 * read a pitch-black room (owner answer 1, 2026-08-27) while the player table is rules-true.
 */
export const CELL_LIGHT_GRADE_PLAYER: ReadonlyArray<readonly [number, number, number, number]> = Object.freeze([
  [1, 1, 1, 0],                 // 0 unseen / no data
  [0.12, 0.12, 0.145, 0.3],     // 1 pitch black
  [0.10, 0.07, 0.15, 0.2],      // 2 magical darkness — violet
  [0.45, 0.45, 0.47, 0.25],     // 3 dim
  [1, 1, 1, 0],                 // 4 bright
  [1, 1, 1, 0],     // 5 sensed: admitted vision, including truesight, stays clear
  [1, 1, 1, 0],
  [1, 1, 1, 0],
]);

export const CELL_LIGHT_GRADE_GM: ReadonlyArray<readonly [number, number, number, number]> = Object.freeze([
  [1, 1, 1, 0],
  [0.34, 0.34, 0.38, 0.3],
  [0.28, 0.22, 0.36, 0.2],
  // Dim for the GM: 0.6 read as "only slightly dim" next to bright (owner 2026-08-28) — 0.5 keeps the
  // room readable while clearly a tier below the yard.
  [0.5, 0.5, 0.53, 0.22],
  [1, 1, 1, 0],
  [0.5, 0.5, 0.5, 0.8],
  [1, 1, 1, 0],
  [1, 1, 1, 0],
]);

/**
 * GM VISION table — the explicit "let me actually read this room" toggle, not the GM's passive
 * baseline. CELL_LIGHT_GRADE_GM is deliberately still dark because the GM's DEFAULT view is meant to
 * look like a dark room; a pitch-black cell there transmits 0.34, which caps the scene however far
 * setSceneLighting() lifts the hemisphere/key/fill. That cap is why GM Vision used to read as "barely
 * brighter" inside a pitch-black room while the corridor outside it visibly changed.
 *
 * Presentation only, exactly like the other two tables: tiers, receipts and cellLightTierAt() are
 * untouched, so nothing here can leak into a rules or perception decision.
 *
 * Darkness still READS as darkness — pitch black stays below dim, magical darkness keeps its violet
 * cast, and sensed keeps its heavy desaturation so "detected, not seen" never masquerades as lit.
 */
export const CELL_LIGHT_GRADE_GM_VISION: ReadonlyArray<readonly [number, number, number, number]> = Object.freeze([
  [1, 1, 1, 0],                 // 0 unseen / no data
  [0.88, 0.88, 0.91, 0.08],     // 1 pitch black
  [0.76, 0.68, 0.84, 0.07],     // 2 magical darkness — violet retained
  [0.94, 0.94, 0.95, 0.05],     // 3 dim
  [1, 1, 1, 0],                 // 4 bright
  [0.78, 0.78, 0.78, 0.45],     // 5 sensed — still grey, still obviously not seen
  [1, 1, 1, 0],
  [1, 1, 1, 0],
]);

export function cellLightGradeTable(
  view: 'gm' | 'player',
  gmVision = false,
): ReadonlyArray<readonly [number, number, number, number]> {
  // gmVision is only honored for the GM view: the toggle is viewer-local GM tooling, and a caster
  // projection must never be brightened by it no matter who is driving the client.
  if (view === 'gm') return gmVision ? CELL_LIGHT_GRADE_GM_VISION : CELL_LIGHT_GRADE_GM;
  return CELL_LIGHT_GRADE_PLAYER;
}

/** 8×1 RGBA8 LUT bytes for the composite shader (transmittance rgb, desaturate in alpha). */
export function cellLightGradeLutBytes(view: 'gm' | 'player', gmVision = false): Uint8Array {
  const table = cellLightGradeTable(view, gmVision);
  const bytes = new Uint8Array(8 * 4);
  table.forEach((entry, tier) => {
    bytes[tier * 4] = Math.round(Math.min(1, Math.max(0, entry[0])) * 255);
    bytes[tier * 4 + 1] = Math.round(Math.min(1, Math.max(0, entry[1])) * 255);
    bytes[tier * 4 + 2] = Math.round(Math.min(1, Math.max(0, entry[2])) * 255);
    bytes[tier * 4 + 3] = Math.round(Math.min(1, Math.max(0, entry[3])) * 255);
  });
  return bytes;
}

export interface CellLightFieldBounds { minX: number; maxX: number; minY: number; maxY: number }

export interface CellLightRoomRect { id: string; x: { min: number; max: number }; y: { min: number; max: number } }

/** Texels claimed by two or more rooms (an overlap or a sub-texel wall) never smooth. */
export const CELL_LIGHT_ROOM_AMBIGUOUS = 0xffff;

/**
 * Room ordinal (+1) per texel, 0 = no room, CELL_LIGHT_ROOM_AMBIGUOUS where rooms overlap. Smoothing
 * never crosses a room boundary (= a wall) and never happens in an ambiguous or roomless texel.
 */
export function rasterizeRoomMask(rooms: CellLightRoomRect[], bounds: CellLightFieldBounds, width: number, height: number): Uint16Array {
  const mask = new Uint16Array(width * height);
  const pxWidthFt = (bounds.maxX - bounds.minX) / width;
  const pxHeightFt = (bounds.maxY - bounds.minY) / height;
  rooms.forEach((room, ordinal) => {
    const minColumn = Math.max(0, Math.floor((room.x.min - bounds.minX) / pxWidthFt));
    const maxColumn = Math.min(width - 1, Math.ceil((room.x.max - bounds.minX) / pxWidthFt) - 1);
    const minRow = Math.max(0, Math.floor((room.y.min - bounds.minY) / pxHeightFt));
    const maxRow = Math.min(height - 1, Math.ceil((room.y.max - bounds.minY) / pxHeightFt) - 1);
    for (let row = minRow; row <= maxRow; row += 1) {
      for (let column = minColumn; column <= maxColumn; column += 1) {
        const index = row * width + column;
        mask[index] = mask[index] === 0 ? ordinal + 1 : CELL_LIGHT_ROOM_AMBIGUOUS;
      }
    }
  });
  return mask;
}

/**
 * Presentation grade per texel (RGBA8: transmittance rgb, desaturate a) from the tier raster, smoothed
 * with a room-aware 2D box kernel (`radiusX`/`radiusY` texels) that averages ONLY texels of the SAME
 * room that carry data, scanning outward from the centre and STOPPING at the first no-data texel or
 * room change in each direction (an unseen cell or a wall is a barrier, not just a skipped sample).
 * Texels outside any room, in an ambiguous (overlapping) texel, or without data keep their own grade.
 * The rules stay per 5 ft cell (the tier raster is untouched); only the picture stops being cubic.
 */
export function buildCellLightGradeRaster(
  tiers: Uint8Array,
  roomMask: Uint16Array | null,
  width: number,
  height: number,
  table: ReadonlyArray<readonly [number, number, number, number]>,
  radiusX: number,
  radiusY: number = radiusX,
): Uint8Array {
  const count = width * height;
  const grade = new Float32Array(count * 4);
  for (let index = 0; index < count; index += 1) {
    const entry = table[tiers[index]!] ?? table[0]!;
    grade[index * 4] = entry[0];
    grade[index * 4 + 1] = entry[1];
    grade[index * 4 + 2] = entry[2];
    grade[index * 4 + 3] = entry[3];
  }
  const rx = Math.max(0, Math.round(radiusX));
  const ry = Math.max(0, Math.round(radiusY));
  if (rx > 0 || ry > 0) {
    const out = new Float32Array(grade);
    const room = (index: number): number => (roomMask === null ? 1 : roomMask[index]!);
    for (let row = 0; row < height; row += 1) {
      for (let column = 0; column < width; column += 1) {
        const index = row * width + column;
        if (tiers[index] === 0) continue;
        const own = room(index);
        if (own === 0 || own === CELL_LIGHT_ROOM_AMBIGUOUS) continue;
        // Reach in each of the four directions is cut at the first barrier, so the kernel is the
        // axis-aligned box that fits inside this texel's uninterrupted run of same-room data.
        let left = 0; while (left < rx && column - left - 1 >= 0 && tiers[index - left - 1] !== 0 && room(index - left - 1) === own) left += 1;
        let right = 0; while (right < rx && column + right + 1 < width && tiers[index + right + 1] !== 0 && room(index + right + 1) === own) right += 1;
        let up = 0; while (up < ry && row - up - 1 >= 0 && tiers[index - (up + 1) * width] !== 0 && room(index - (up + 1) * width) === own) up += 1;
        let down = 0; while (down < ry && row + down + 1 < height && tiers[index + (down + 1) * width] !== 0 && room(index + (down + 1) * width) === own) down += 1;
        let r = 0; let g = 0; let b = 0; let a = 0; let weight = 0;
        for (let dy = -up; dy <= down; dy += 1) {
          const rowOffset = (row + dy) * width;
          for (let dx = -left; dx <= right; dx += 1) {
            const other = rowOffset + column + dx;
            if (tiers[other] === 0 || room(other) !== own) continue;
            r += grade[other * 4]!; g += grade[other * 4 + 1]!; b += grade[other * 4 + 2]!; a += grade[other * 4 + 3]!;
            weight += 1;
          }
        }
        if (weight > 0) {
          out[index * 4] = r / weight; out[index * 4 + 1] = g / weight; out[index * 4 + 2] = b / weight; out[index * 4 + 3] = a / weight;
        }
      }
    }
    grade.set(out);
  }
  const bytes = new Uint8Array(count * 4);
  for (let index = 0; index < count * 4; index += 1) bytes[index] = Math.round(Math.min(1, Math.max(0, grade[index]!)) * 255);
  return bytes;
}

export interface CellLightRaster {
  data: Uint8Array;
  width: number;
  height: number;
  histogram: number[];
  /** true when at least one texel carries a tier that changes the picture */
  grades: boolean;
}

/**
 * Nearest-cell rasterization of the selected floors into a field texture (no feather, no blur:
 * a wall boundary must stay a wall boundary). Texel (0,0) is the field's minimum X/Y corner.
 */
export function rasterizeCellLightField(
  floors: Array<{ floor: Ft3dCellLightFloor; display: Uint8Array }>,
  cellSizeFt: number,
  bounds: CellLightFieldBounds,
  width: number,
  height: number,
  sampleAllowed?: (floor: Ft3dCellLightFloor, xFt: number, yFt: number) => boolean,
): CellLightRaster {
  const data = new Uint8Array(width * height);
  const histogram = new Array<number>(8).fill(0);
  const pxWidthFt = (bounds.maxX - bounds.minX) / width;
  const pxHeightFt = (bounds.maxY - bounds.minY) / height;
  let grades = false;
  for (const { floor, display } of floors) {
    const minColumn = Math.max(0, Math.floor((floor.originX - bounds.minX) / pxWidthFt));
    const maxColumn = Math.min(width - 1, Math.ceil((floor.originX + floor.columns * cellSizeFt - bounds.minX) / pxWidthFt));
    const minRow = Math.max(0, Math.floor((floor.originY - bounds.minY) / pxHeightFt));
    const maxRow = Math.min(height - 1, Math.ceil((floor.originY + floor.rows * cellSizeFt - bounds.minY) / pxHeightFt));
    for (let row = minRow; row <= maxRow; row += 1) {
      const yFt = bounds.minY + (row + 0.5) * pxHeightFt;
      for (let column = minColumn; column <= maxColumn; column += 1) {
        const xFt = bounds.minX + (column + 0.5) * pxWidthFt;
        if (sampleAllowed && !sampleAllowed(floor, xFt, yFt)) continue;
        const tier = cellLightTierAt(floor, display, xFt, yFt, cellSizeFt);
        if (tier === CELL_LIGHT_TIER.unseen) continue;
        const index = row * width + column;
        // Stacked floors never share a lattice here (caller selects one floor), but ragged
        // room edges can: keep the BRIGHTER tier (by light, not by code) so a doorway reads lit.
        if (cellLightBrightnessRank(tier) > cellLightBrightnessRank(data[index] ?? 0)) data[index] = tier;
      }
    }
  }
  for (let index = 0; index < data.length; index += 1) {
    const tier = data[index]!;
    histogram[tier] = (histogram[tier] ?? 0) + 1;
    if (tier !== CELL_LIGHT_TIER.unseen && tier !== CELL_LIGHT_TIER.bright) grades = true;
  }
  return { data, width, height, histogram, grades };
}
